import { apiRequest } from '../services/apiService';

export type CrewIdentityDocumentType = 'drivers_license' | 'state_id' | 'passport' | 'other';

export interface CrewIdentityDocumentMetadata {
  hasDocument: boolean;
  documentType?: CrewIdentityDocumentType;
  contentType?: string;
  size?: number;
  expiresOn?: string | null;
  uploadedAt?: string;
  updatedAt?: string;
}

function responseData<T>(response: unknown): T {
  const axiosResponse = response as { data?: { data?: T } | T };
  const body = axiosResponse?.data;
  if (body && typeof body === 'object' && 'data' in body) {
    return (body as { data: T }).data;
  }
  return body as T;
}

function requestError(error: unknown, fallback: string): Error {
  const requestFailure = error as {
    response?: { data?: { message?: unknown } };
    message?: unknown;
  };
  const apiMessage = requestFailure?.response?.data?.message;
  const genericMessage = requestFailure?.message;
  return new Error(
    (typeof apiMessage === 'string' && apiMessage) ||
      (typeof genericMessage === 'string' && genericMessage) ||
      fallback,
  );
}

export async function getCrewIdentityDocument(
  contractorId?: string,
): Promise<CrewIdentityDocumentMetadata> {
  const { response, error } = await apiRequest({
    method: 'GET',
    url: contractorId
      ? `crew-identity-document/contractor/${contractorId}`
      : 'crew-identity-document/me',
    isToken: true,
  });
  if (error || !response) throw requestError(error, 'Could not load Government ID');
  return responseData<CrewIdentityDocumentMetadata>(response);
}

export async function uploadMyCrewIdentityDocument(input: {
  file: File;
  documentType: CrewIdentityDocumentType;
  expiresOn?: string;
  tempToken?: string | null;
}): Promise<CrewIdentityDocumentMetadata> {
  const formData = new FormData();
  formData.append('file', input.file);
  formData.append('documentType', input.documentType);
  if (input.expiresOn) formData.append('expiresOn', input.expiresOn);

  const { response, error } = await apiRequest({
    method: 'POST',
    url: 'crew-identity-document/me',
    data: formData,
    isToken: true,
    tempToken: input.tempToken,
  });
  if (error || !response) throw requestError(error, 'Could not upload Government ID');
  return responseData<CrewIdentityDocumentMetadata>(response);
}

export async function updateMyCrewIdentityDocument(input: {
  documentType: CrewIdentityDocumentType;
  expiresOn?: string;
}): Promise<CrewIdentityDocumentMetadata> {
  const { response, error } = await apiRequest({
    method: 'PATCH',
    url: 'crew-identity-document/me',
    data: {
      documentType: input.documentType,
      expiresOn: input.expiresOn || null,
    },
    isToken: true,
  });
  if (error || !response) throw requestError(error, 'Could not update Government ID');
  return responseData<CrewIdentityDocumentMetadata>(response);
}

export async function getCrewIdentityDocumentAccessUrl(
  contractorId?: string,
): Promise<{ url: string; expiresIn: number }> {
  const { response, error } = await apiRequest({
    method: 'POST',
    url: contractorId
      ? `crew-identity-document/contractor/${contractorId}/access-url`
      : 'crew-identity-document/me/access-url',
    isToken: true,
  });
  if (error || !response) throw requestError(error, 'Could not open Government ID');
  return responseData<{ url: string; expiresIn: number }>(response);
}

export async function deleteMyCrewIdentityDocument(): Promise<void> {
  const { response, error } = await apiRequest({
    method: 'DELETE',
    url: 'crew-identity-document/me',
    isToken: true,
  });
  if (error || !response) throw requestError(error, 'Could not delete Government ID');
}
